import React, { useState } from 'react';
import { ArrowLeft, Globe, ShieldCheck, ShieldOff, Archive, GitBranch, Terminal, Loader as Loader2, RefreshCw, TriangleAlert as AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import type { XCloudSite } from '@/types/xcloud';
import {
  useSiteSsl, useSiteBackups, useSiteMonitoring,
  useSiteEvents, useDeploymentLogs, useSiteGit, useSiteSsh,
  useTriggerBackup, usePurgeCache,
} from '@/hooks/useXcloud';

type Tab = 'overview' | 'ssl' | 'backups' | 'monitoring' | 'events' | 'deployments' | 'git' | 'ssh';

interface Props {
  site: XCloudSite;
  workspaceId: string;
  canManage: boolean;
  onClose: () => void;
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'border-sky-500 text-sky-600 dark:text-sky-400'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
      }`}
    >
      {children}
    </button>
  );
}

function StatusPill({ status }: { status: string }) {
  const labelMap: Record<string, string> = {
    active: 'Active',
    provisioned: 'Active',
    inactive: 'Inactive',
    provisioning: 'Provisioning',
    failed: 'Failed',
  };
  const colorMap: Record<string, string> = {
    active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    provisioned: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    inactive: 'bg-muted text-muted-foreground',
    provisioning: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    failed: 'bg-red-500/10 text-red-500',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] ?? 'bg-muted text-muted-foreground'}`}>
      {labelMap[status] ?? status}
    </span>
  );
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 px-4 py-3 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground shrink-0 w-40">{label}</span>
      <span className="text-sm text-foreground text-right flex-1 min-w-0 break-all">{value ?? '—'}</span>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="py-6 px-4 space-y-4 animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-14 bg-muted rounded-lg" />
      ))}
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function ErrorTabState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
        <AlertTriangle className="w-6 h-6 text-red-500" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">Failed to load data</p>
      <p className="text-xs text-muted-foreground max-w-xs">{message}</p>
    </div>
  );
}

export function SiteDetailDrawer({ site, workspaceId, canManage, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const { data: sslData, isLoading: sslLoading, error: sslError } = useSiteSsl(workspaceId, activeTab === 'ssl' ? site.uuid : undefined);
  const { data: backupsData, isLoading: backupsLoading, error: backupsError } = useSiteBackups(workspaceId, activeTab === 'backups' ? site.uuid : undefined);
  const { data: monData, isLoading: monLoading, error: monError } = useSiteMonitoring(workspaceId, activeTab === 'monitoring' ? site.uuid : undefined);
  const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useSiteEvents(workspaceId, activeTab === 'events' ? site.uuid : undefined);
  const { data: deployData, isLoading: deployLoading, error: deployError } = useDeploymentLogs(workspaceId, activeTab === 'deployments' ? site.uuid : undefined);
  const { data: gitData, isLoading: gitLoading, error: gitError } = useSiteGit(workspaceId, activeTab === 'git' ? site.uuid : undefined);
  const { data: sshData, isLoading: sshLoading, error: sshError } = useSiteSsh(workspaceId, activeTab === 'ssh' ? site.uuid : undefined);

  const backupMutation = useTriggerBackup(workspaceId);
  const purgeMutation = usePurgeCache(workspaceId);

  const displayName = site.title || site.name || site.domain_name || site.domain || 'Site';

  const handleBackup = async () => {
    try {
      await backupMutation.mutateAsync(site.uuid);
      toast.success('Backup triggered successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to trigger backup');
    }
  };

  const handlePurge = async () => {
    try {
      await purgeMutation.mutateAsync(site.uuid);
      toast.success('Cache purged successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to purge cache');
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'ssl', label: 'SSL' },
    { id: 'backups', label: 'Backups' },
    { id: 'monitoring', label: 'Monitoring' },
    { id: 'events', label: 'Events' },
    { id: 'deployments', label: 'Deployments' },
    { id: 'git', label: 'Git' },
    { id: 'ssh', label: 'SSH' },
  ];

  return (
    <div className="w-full">
      {/* Back bar */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Hosting
        </button>
      </div>

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center shrink-0">
            <Globe className="w-6 h-6 text-sky-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{displayName}</h1>
            <div className="flex items-center gap-3 mt-1">
              <StatusPill status={site.status} />
              {site.server_name && (
                <span className="text-sm text-muted-foreground">{site.server_name}</span>
              )}
              {site.php_version && (
                <span className="text-sm text-muted-foreground">PHP {site.php_version}</span>
              )}
            </div>
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleBackup}
              disabled={backupMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted text-sm font-medium text-foreground transition-colors disabled:opacity-50"
            >
              {backupMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
              Backup
            </button>
            <button
              onClick={handlePurge}
              disabled={purgeMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted text-sm font-medium text-foreground transition-colors disabled:opacity-50"
            >
              {purgeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Purge Cache
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-border mb-6 overflow-x-auto">
        {tabs.map(t => (
          <TabBtn key={t.id} active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </TabBtn>
        ))}
      </div>

      {/* Tab content */}
      <div className="w-full">
        {activeTab === 'overview' && (
          <div className="bg-card rounded-xl border border-border divide-y divide-border">
            <DataRow label="Domain" value={site.domain_name || site.domain || site.name} />
            <DataRow label="Server" value={site.server_name} />
            <DataRow label="Type" value={site.type} />
            <DataRow label="PHP Version" value={site.php_version ? `PHP ${site.php_version}` : undefined} />
            <DataRow label="SSL" value={site.ssl?.status === 'active' ? (
              <span className="flex items-center gap-1 justify-end">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Active
              </span>
            ) : (
              <span className="flex items-center gap-1 justify-end">
                <ShieldOff className="w-3.5 h-3.5 text-muted-foreground" /> {site.ssl?.status || 'Inactive'}
              </span>
            )} />
            <DataRow label="Created" value={site.created_at ? new Date(site.created_at).toLocaleDateString() : undefined} />
          </div>
        )}

        {activeTab === 'ssl' && (
          sslLoading ? <LoadingState /> :
          sslError ? <ErrorTabState message={(sslError as Error).message} /> :
          !sslData?.data ? <EmptyState label="No SSL information available" /> :
          <div className="bg-card rounded-xl border border-border divide-y divide-border">
            <DataRow label="Provider" value={(sslData.data as any).provider} />
            <DataRow label="Status" value={sslData.data.status} />
            <DataRow label="Expires" value={sslData.data.expires_at ? new Date(sslData.data.expires_at).toLocaleDateString() : undefined} />
            {sslData.data.domains?.length && (
              <DataRow label="Domains" value={sslData.data.domains.join(', ')} />
            )}
          </div>
        )}

        {activeTab === 'backups' && (
          backupsLoading ? <LoadingState /> :
          backupsError ? <ErrorTabState message={(backupsError as Error).message} /> :
          !backupsData?.data?.length ? <EmptyState label="No backups found" /> :
          <div className="space-y-2">
            {backupsData.data.map(b => (
              <div key={b.id} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <Archive className="w-4 h-4 text-sky-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{b.type || 'Backup'}</p>
                  {b.created_at && (
                    <p className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString()}</p>
                  )}
                </div>
                {b.size && <span className="text-xs text-muted-foreground shrink-0">{(b.size / 1024 / 1024).toFixed(1)} MB</span>}
                {b.status && <span className="text-xs text-muted-foreground capitalize shrink-0 px-2 py-0.5 rounded-full bg-muted">{b.status}</span>}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'monitoring' && (
          monLoading ? <LoadingState /> :
          monError ? <ErrorTabState message={(monError as Error).message} /> :
          !monData?.data ? <EmptyState label="No monitoring data available" /> :
          <div className="space-y-3">
            {[
              { label: 'CPU Usage', value: monData.data.cpu_usage, suffix: '%' },
              { label: 'Memory Usage', value: monData.data.memory_usage, suffix: '%' },
              { label: 'Response Time', value: monData.data.response_time, suffix: 'ms' },
            ].map(m => m.value !== undefined && (
              <div key={m.label} className="p-4 rounded-xl bg-card border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">{m.label}</span>
                  <span className="text-sm font-semibold text-foreground">{m.value}{m.suffix}</span>
                </div>
                {m.suffix === '%' && (
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${Math.min(100, m.value as number)}%` }} />
                  </div>
                )}
              </div>
            ))}
            {monData.data.php_fpm && (
              <div className="p-4 rounded-xl bg-card border border-border">
                <p className="text-sm font-medium text-foreground mb-3">PHP-FPM</p>
                <div className="flex gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Active</p>
                    <p className="text-lg font-semibold text-foreground">{monData.data.php_fpm.active_processes}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Idle</p>
                    <p className="text-lg font-semibold text-foreground">{monData.data.php_fpm.idle_processes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'events' && (
          eventsLoading ? <LoadingState /> :
          eventsError ? <ErrorTabState message={(eventsError as Error).message} /> :
          !eventsData?.data?.length ? <EmptyState label="No events found" /> :
          <div className="space-y-2">
            {eventsData.data.map((e, i) => (
              <div key={e.uuid ?? i} className="p-4 rounded-xl bg-card border border-border">
                <p className="text-sm text-foreground">{e.description}</p>
                {e.created_at && <p className="text-xs text-muted-foreground mt-1">{new Date(e.created_at).toLocaleString()}</p>}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'deployments' && (
          deployLoading ? <LoadingState /> :
          deployError ? <ErrorTabState message={(deployError as Error).message} /> :
          !deployData?.data?.length ? <EmptyState label="No deployment logs found" /> :
          <div className="space-y-2">
            {deployData.data.map(d => (
              <div key={d.uuid ?? d.id} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <GitBranch className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  {d.branch && <p className="text-sm font-medium text-foreground">{d.branch}</p>}
                  {d.commit && <p className="text-xs text-muted-foreground font-mono truncate">{d.commit.slice(0, 8)}</p>}
                  {d.created_at && <p className="text-xs text-muted-foreground mt-0.5">{new Date(d.created_at).toLocaleString()}</p>}
                </div>
                <span className={`text-xs font-medium capitalize shrink-0 px-2 py-0.5 rounded-full ${d.status === 'success' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                  {d.status}
                </span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'git' && (
          gitLoading ? <LoadingState /> :
          gitError ? <ErrorTabState message={(gitError as Error).message} /> :
          !gitData?.data ? <EmptyState label="No Git repository connected" /> :
          <div className="bg-card rounded-xl border border-border divide-y divide-border">
            <DataRow label="Repository" value={gitData.data.repository} />
            <DataRow label="Branch" value={gitData.data.branch} />
            <DataRow label="Provider" value={gitData.data.provider} />
            <DataRow label="Auto Deploy" value={gitData.data.auto_deploy ? 'Yes' : 'No'} />
            <DataRow label="Push Deploy" value={gitData.data.push_deploy_enabled ? 'Enabled' : 'Disabled'} />
          </div>
        )}

        {activeTab === 'ssh' && (
          sshLoading ? <LoadingState /> :
          sshError ? <ErrorTabState message={(sshError as Error).message} /> :
          !sshData?.data ? <EmptyState label="No SSH configuration found" /> :
          <div className="space-y-4">
            <div className="bg-card rounded-xl border border-border divide-y divide-border">
              <DataRow label="Host" value={sshData.data.host} />
              <DataRow label="Username" value={sshData.data.username || sshData.data.site_user} />
              <DataRow label="Port" value={sshData.data.port} />
              <DataRow label="Auth Mode" value={sshData.data.authentication_mode} />
              <DataRow label="SFTP" value={sshData.data.sftp_enabled ? 'Enabled' : 'Disabled'} />
            </div>
            {sshData.data.ssh_keypairs && sshData.data.ssh_keypairs.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">SSH Key Pairs</h3>
                <div className="space-y-2">
                  {sshData.data.ssh_keypairs.map(kp => (
                    <div key={kp.uuid} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border">
                      <Terminal className="w-4 h-4 text-sky-500 shrink-0" />
                      <span className="text-sm text-foreground flex-1">{kp.name}</span>
                      <span className="text-xs text-muted-foreground font-mono truncate max-w-[160px]">{kp.fingerprint}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
